import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';
import { InfoTable, CustomAutocomplete, RemovePlayerFromTeamModal } from '@myplay/ui';

import { ClubAthletesTableItem, HeaderTiles, SceneLoaderWrapper } from '../../components';
import { toTitleCase } from '../../utils/stringManipulations';
import sort from '../../utils/sort';
import { FULL_NAME, CATEGORY, HIGHLIGHTS_COUNT, TEAM_NAME, PLAYER_NUMBER, BIRTHDATE } from '../../utils/constants';
import I18N from '../../I18N';
import "./Athletes.scss";

const headers = ["Name", "Jersey", "Team", "Sport", "Age", "Recorded HL", ""];

const Athletes = ({ TeamsStore, MetricsStore, ClubsStore }) => {
  const { teams, removePlayerFromTeam, getTeamNameById } = TeamsStore;
  const [allTeamMembers, setAllTeamMembers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState({});
  const [isDesc, setIsDesc] = useState(false);
  const [sortedBy, setSortedBy] = useState("");
  const [filteredAthletes, setFilteredAthletes] = useState([]);
  const [playerToDelete, setPlayerToDelete] = useState(null);
  const [shouldDisplayRemovePlayerModal, setRemovePlayerModal] = useState(false);

  useEffect(() => {
    MetricsStore.setClubMetrics();
    processAllClubMembers(teams);
  }, [TeamsStore.teams, ClubsStore.currentClub]);

  const processAllClubMembers = teams => {
    const teamMembers = [];
    teams.map(team => {
      team.members.players.map(player => {
        const indexOfCurrentPlayer = teamMembers.findIndex(currentPlayer => currentPlayer._id === player._id);
        if (indexOfCurrentPlayer === -1) {
          teamMembers.push({
            ...player,
            category: team.category.name,
            teamName: team.name,
            season: team.year
          });
        } else {
          teamMembers[indexOfCurrentPlayer].category = team.category.name;
          teamMembers[indexOfCurrentPlayer].teamName = team.name;
          teamMembers[indexOfCurrentPlayer].season = team.year;
        }
      });
    });
    setAllTeamMembers(teamMembers);
    setFilteredAthletes(teamMembers);
  }

  const handleSelect = player => {
    setSelectedPlayer(player);
  };

  const handleTableItemClick = childProps => {
    const { athlete } = childProps;
    if (athlete._id === selectedPlayer._id) {
      setSelectedPlayer({});
      return;
    };

    setSelectedPlayer(athlete);
  }

  const handleHeaderClick = header => {
    let sorted = [];
    switch (header) {
      case "Jersey":
        sorted = [...sort(filteredAthletes, PLAYER_NUMBER)];
        break;
      case "Team":
        sorted = [...sort(filteredAthletes, TEAM_NAME)];
        break;
      case "Sport":
        sorted = [...sort(filteredAthletes, CATEGORY)];
        break;
      case "Age":
        sorted = [...sort(filteredAthletes, BIRTHDATE)];
        break;
      case "Recorded HL":
        sorted = [...sort(filteredAthletes, HIGHLIGHTS_COUNT)];
        break;
      case "Name":
        sorted = [...sort(filteredAthletes, FULL_NAME)];
        break;
    }

    if (isDesc) {
      sorted.reverse();
    };
    setIsDesc(!isDesc);
    setFilteredAthletes([...sorted]);
    setSortedBy(header);
  }

  const handleSearchOnChange = (value, filteredAthletes) => {
    if (!value) {
      setFilteredAthletes([...allTeamMembers]);
      return;
    };

    setFilteredAthletes([...filteredAthletes]);
  };

  const handleDeletePlayer = (player, event) => {
    event.stopPropagation();
    const playerCopy = { ...player };
    const teamsCopy = [];
    player.teams.map(teamId => {
      const teamName = getTeamNameById(teamId);
      teamsCopy.push({
        name: teamName,
        _id: teamId
      });
    });

    playerCopy.teams = [...teamsCopy];
    setPlayerToDelete(playerCopy);
    setRemovePlayerModal(true);
  };

  const handleCloseRemovePlayerModal = () => {
    setPlayerToDelete(null);
    setRemovePlayerModal(false);
  };

  const handleApproveDeletePlayer = async team => {
    await removePlayerFromTeam(playerToDelete, team);
    processAllClubMembers(teams);
    handleCloseRemovePlayerModal();
  };

  return (
    <SceneLoaderWrapper>
      <div className="athletes" data-testid="athletes">
        <div className="athletes__header">
          <div className="athletes__header__filter-box">
            <p className="athletes__header__filter-box__title">{I18N.t("MY_ATHLETES").toUpperCase()}</p>
            <CustomAutocomplete
              placeholder={toTitleCase(I18N.t("SEARCH_ATHLETE"))}
              items={allTeamMembers}
              propertyToFilterBy={FULL_NAME}
              onSelect={handleSelect}
              onChange={handleSearchOnChange}
            />
          </div>
          <HeaderTiles />
        </div>
        <InfoTable
          gridTemplateColumns="25% 6% auto 10% 6% 10% 14%"
          headers={headers}
          onChildClick={handleTableItemClick}
          onHeaderClick={handleHeaderClick}
          sortedBy={sortedBy}
          isDesc={isDesc}
        >
          {filteredAthletes.map(member =>
            <ClubAthletesTableItem
              athlete={member}
              isSelected={member._id === selectedPlayer._id}
              onDelete={event => handleDeletePlayer(member, event)}
            />)}
        </InfoTable>
        {playerToDelete &&
          <RemovePlayerFromTeamModal
            isVisible={shouldDisplayRemovePlayerModal}
            name={playerToDelete.fullName}
            teams={playerToDelete.teams}
            onClose={handleCloseRemovePlayerModal}
            onApprove={handleApproveDeletePlayer}
          />
        }
      </div>
    </SceneLoaderWrapper>
  );
};

Athletes.propTypes = {
  MetricsStore: PropTypes.object,
  TeamsStore: PropTypes.object
};

Athletes.defaultProps = {
  MetricsStore: {},
  TeamsStore: {}
};

export default inject("TeamsStore", "MetricsStore", "ClubsStore")(observer(Athletes));